
import React, { useState, useEffect, useRef } from 'react';
import { SignedIn, SignedOut, UserButton, SignInButton } from "@clerk/clerk-react";
import Button from './Button';
import ListBulletIcon from './icons/ListBulletIcon';
import ChartPieIcon from './icons/ChartPieIcon';
import UserCircleIcon from './icons/UserCircleIcon';
import PlusCircleIcon from './icons/PlusCircleIcon';
import Logo from './Logo';
import HelpMenu from './HelpMenu';
import { useTranslation } from 'react-i18next';
import { UI_LANGUAGES } from '@/constants';

interface HeaderProps {
  onLogoClick: () => void;
  onSolveClick: () => void;
  onHistoryClick: () => void;
  onDashboardClick: () => void;
  onAccountClick: () => void;
  onHelperClick: () => void;
}

const LanguageSwitcher: React.FC = () => {
  const { i18n } = useTranslation();
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const langMenuRef = useRef<HTMLDivElement>(null);

  const handleLanguageChange = (langCode: string) => {
    i18n.changeLanguage(langCode);
    setIsLangMenuOpen(false);
  };

  const currentLanguage = UI_LANGUAGES.find(lang => i18n.language.startsWith(lang.code)) || UI_LANGUAGES[0];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (langMenuRef.current && !langMenuRef.current.contains(event.target as Node)) {
        setIsLangMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={langMenuRef}>
      <Button onClick={() => setIsLangMenuOpen(!isLangMenuOpen)} variant="ghost" size="icon" aria-label="Change language" className="!w-10 !h-10">
        <span className="text-2xl">{currentLanguage.flag}</span>
      </Button>
      {isLangMenuOpen && (
        <div className="absolute right-0 mt-2 w-40 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 py-1 z-50">
          {UI_LANGUAGES.map(lang => ( <button key={lang.code} onClick={() => handleLanguageChange(lang.code)} className={`w-full text-left px-4 py-2 text-sm flex items-center space-x-2 ${ currentLanguage.code === lang.code ? 'bg-sky-100 text-sky-700 font-semibold' : 'text-gray-700 hover:bg-gray-100' }`} > <span>{lang.flag}</span> <span>{lang.name}</span> </button> ))}
        </div>
      )}
    </div>
  );
};

const Header: React.FC<HeaderProps> = ({ onLogoClick, onSolveClick, onHistoryClick, onDashboardClick, onAccountClick, onHelperClick }) => {
  const { t, i18n } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const langMenuRef = useRef<HTMLDivElement>(null);

  const handleLanguageChange = (langCode: string) => {
    i18n.changeLanguage(langCode);
    setIsLangMenuOpen(false);
  };

  const currentLanguage = UI_LANGUAGES.find(lang => i18n.language.startsWith(lang.code)) || UI_LANGUAGES[0];

  // Handle header style on scroll
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    // Initial check in case the page is already scrolled on load
    handleScroll();
    // Cleanup listener on component unmount
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle clicks outside of language menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (langMenuRef.current && !langMenuRef.current.contains(event.target as Node)) {
        setIsLangMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <>
      <header
        className={`w-full sticky top-0 z-40 print:hidden transition-all duration-300 ease-in-out ${
          isScrolled ? 'bg-white/80 backdrop-blur-sm border-b border-slate-200 shadow-sm' : 'bg-transparent border-b border-transparent'
        }`}
      >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Logo onClick={onLogoClick} />
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-2">
            <Button
              onClick={onSolveClick}
              title={t('header.solveNewProblemTitle')}
              aria-label={t('header.solveNewProblemTitle')}
              size="md"
              className="space-x-2 font-medium"
            >
              <PlusCircleIcon className="w-5 h-5" />
              <span>{t('header.solveProblem')}</span>
            </Button>
            <Button
              onClick={onDashboardClick}
              title={t('header.dashboardTitle')}
              aria-label={t('header.dashboardAriaLabel')}
              variant="ghost"
              size="base"
              className="font-medium space-x-2"
            >
              <ChartPieIcon className="w-5 h-5 text-sky-600" />
              <span>{t('header.dashboard')}</span>
            </Button>
            <Button
              onClick={onHistoryClick}
              title={t('header.solutionsTitle')}
              aria-label={t('header.solutionsAriaLabel')}
              variant="ghost"
              size="base"
              className="font-medium space-x-2"
            >
              <ListBulletIcon className="w-5 h-5 text-sky-600" />
              <span>{t('header.solutions')}</span>
            </Button>
            <Button onClick={onAccountClick} title={t('header.accountTitle')} variant="ghost" size="base" className="font-medium space-x-2" aria-label={t('header.accountAriaLabel')}>
              <UserCircleIcon className="w-5 h-5 text-sky-600" />
              <span>{t('header.account')}</span>
            </Button>
            <HelpMenu onHelperClick={onHelperClick} />

            <LanguageSwitcher />
            <SignedIn>
              <div className="ml-2">
                <UserButton />
              </div>
            </SignedIn>
            <SignedOut>
              {/* The SignInButton will open a modal for login/registration */}
              <SignInButton mode="modal" />
            </SignedOut>
          </nav>

          {/* Mobile right side */}
          <div className="md:hidden flex items-center space-x-2">
            <LanguageSwitcher />
            <SignedIn>
              <UserButton />
            </SignedIn>
            <SignedOut>
              <SignInButton mode="modal">
                <Button variant="ghost">{t('auth.login')}</Button>
              </SignInButton>
            </SignedOut>
          </div>
        </div>
      </div>
      </header>
    </>
  );
};

export default Header;